import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'
import type { PesticideDetail, PesticideLabelStatus, PesticideProduct, PesticideUsePattern } from './types'

const BROWSE_LIMIT = 50

/** One-shot search by name or active ingredient, for the command palette (no hook needed there). */
export async function searchPesticides(q: string, limit = 25): Promise<PesticideProduct[]> {
  if (!q.trim()) return []
  return api.get<PesticideProduct[]>(`/pesticides?q=${encodeURIComponent(q.trim())}&limit=${limit}`)
}

/**
 * Type-ahead for choosing a product to log: current registrations for one crop (blueberry), ranked
 * with names that start with what was typed first. Drop the filters to search the whole registry.
 */
export async function searchProducts(q: string, opts: { allProducts?: boolean; limit?: number } = {}): Promise<PesticideProduct[]> {
  if (q.trim().length < 2) return []
  const params = new URLSearchParams({ q: q.trim(), limit: String(opts.limit ?? 8) })
  if (!opts.allProducts) {
    params.set('crop', 'BLUEBERRY')
    params.set('current', 'true')
  }
  return api.get<PesticideProduct[]>(`/pesticides?${params}`)
}

/** One-shot fetch for the detail page. Includes label data (see PesticideDetail), unlike search/browse rows. */
export async function getPesticide(id: string): Promise<PesticideDetail> {
  return api.get<PesticideDetail>(`/pesticides/${id}`)
}

/** The lightweight poll for a product whose label is still processing: just the status, no product or label data. */
export async function getPesticideLabelStatus(id: string): Promise<PesticideLabelStatus> {
  return api.get<PesticideLabelStatus>(`/pesticides/${id}/label-status`)
}

/** A label use pattern that applies to blueberries (the crop text or its crop group says so). */
export const isBlueberry = (p: PesticideUsePattern) => /blueberr/i.test(`${p.cropRaw} ${p.cropGroup ?? ''}`)

const POLL_INTERVAL_MS = 3_000
const POLL_MAX_MS = 3 * 60_000

/**
 * One product with its label, for the detail page and the spray wizard. The first load also kicks
 * off label processing on the server. While the label is `processing`, polls the lightweight status
 * route; once it settles, loads the full detail one more time. `id` undefined loads nothing.
 */
export function usePesticideDetail(id: string | undefined) {
  const [pesticide, setPesticide] = useState<PesticideDetail | null>(null)
  const [loading, setLoading] = useState(!!id)
  const [error, setError] = useState<string | null>(null)
  const [pollTimedOut, setPollTimedOut] = useState(false)

  useEffect(() => {
    setPesticide(null)
    setError(null)
    setPollTimedOut(false)
    if (!id) {
      setLoading(false)
      return
    }
    const productId = id
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const startedAt = Date.now()
    setLoading(true)

    async function poll() {
      try {
        const next = await getPesticideLabelStatus(productId)
        if (cancelled) return
        if (next.status === 'processing') {
          setPesticide((prev) => (prev ? { ...prev, label: next } : prev)) // just moves the progress step along
          if (Date.now() - startedAt < POLL_MAX_MS) timer = setTimeout(poll, POLL_INTERVAL_MS)
          else setPollTimedOut(true)
        } else {
          const data = await getPesticide(productId)
          if (!cancelled) setPesticide(data)
        }
      } catch {
        // A failed poll just stops polling; the caller keeps what it has.
      }
    }

    getPesticide(productId)
      .then((data) => {
        if (cancelled) return
        setPesticide(data)
        if (data.label.status === 'processing') timer = setTimeout(poll, POLL_INTERVAL_MS)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load this product.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [id])

  return { pesticide, loading, error, pollTimedOut }
}

/**
 * Backs the /pesticides browse page: `query` empty browses alphabetically (cursor-paginated,
 * "load more"); non-empty switches to a server-side search (no pagination, just a capped list).
 * A request-generation guard means a slow, stale response can never overwrite a newer one's results.
 */
export function usePesticides(query: string) {
  const [items, setItems] = useState<PesticideProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const requestId = useRef(0)

  const load = useCallback(async (q: string) => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    try {
      if (q.trim()) {
        const results = await searchPesticides(q)
        if (id !== requestId.current) return
        setItems(results)
        setNextCursor(null)
      } else {
        const page = await api.getPage<PesticideProduct>(`/pesticides?limit=${BROWSE_LIMIT}`)
        if (id !== requestId.current) return
        setItems(page.data)
        setNextCursor(page.nextCursor)
      }
    } catch (err) {
      if (id !== requestId.current) return
      setError(err instanceof Error ? err.message : 'Could not load pesticides.')
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(query)
  }, [query, load])

  async function loadMore() {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await api.getPage<PesticideProduct>(`/pesticides?cursor=${encodeURIComponent(nextCursor)}&limit=${BROWSE_LIMIT}`)
      setItems((prev) => [...prev, ...page.data])
      setNextCursor(page.nextCursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load more pesticides.')
    } finally {
      setLoadingMore(false)
    }
  }

  return { items, loading, loadingMore, error, hasMore: nextCursor !== null, loadMore }
}
